import buildUrl from '@framework/url/helpers/buildUrl';
import domain from '@framework/config/domain';
import shouldUseInternalNetwork from '@framework/router/Next/shouldUseInternalNetwork';
import isServer from '@framework/router/Next/isServer';
import creditFlowConfig from '@creditflowresearch/config';
import URL from '@/creditflowresearch/Router/urlConstants';
import usersUrl from '@users/url';

const url = {
  getUrl(...uris: (string | string[])[]): string {
    return buildUrl(domain.protocol, creditFlowConfig.hostname, ...uris);
  },

  getTraceUrl(): string {
    return this.getUrl(`${ URL.TRACE }-abs`);
  },

  getBondsInventoryABSUrl(): string {
    return this.getUrl(`${ URL.BONDS_INVENTORIES }-abs`);
  },

  getProductLoanLevelUrl(): string {
    return this.getUrl(URL.PRODUCT_US_ABS_EE);
  },

  getPortfolioMonitorUrl(): string {
    return this.getUrl(URL.PORTFOLIO_MONITOR);
  },

  getProductPipelineUrl(): string {
    return this.getUrl(URL.PRODUCT_US_ABS_PIPELINE);
  },

  getPricingPageUrl(): string {
    return this.getUrl(URL.PRICING);
  },

  getAbsActiveCalendarUrl(): string {
    return this.getUrl(URL.ABS_ACTIVE_CALENDAR);
  },

  getCorporateActiveCalendarUrl(): string {
    return this.getUrl(URL.CORPORATE_ACTIVE_CALENDAR);
  },

  getEuropeActiveCalendarUrl(): string {
    return this.getUrl(URL.EUROPE_ACTIVE_CALENDAR);
  },

  getApiUrl(uris: string | string[] = []): string {
    return this.getUrl('/api', uris);
  },

  getPropagateCallbackUrl(url: string): string {
    const callbackUrl = this.getUrl(url);
    return this.getApiUrl(`/propagate?callbackUrl=${ encodeURIComponent(callbackUrl) }`);
  },

  getUsersLogoutPath(callbackUrl = window.location.href) {
    return `${ usersUrl.getExternalUrl() }/api/session/logout?callbackUrl=${ encodeURIComponent(callbackUrl) }`;
  },

  getProxyLogoutPath(callBackUrl = ''): string {
    return this.getUrl(`/proxy/users/api/session/logout?callbackUrl=${ encodeURIComponent(callBackUrl) }`);
  },

  getLogoutUrl(callBackUrl = ''): string {
    return this.getProxyLogoutPath(this.getUsersLogoutPath(this.getUrl(callBackUrl)));
  },

  getAbsCfxIndicesUrl(): string {
    return this.getUrl(URL.ABS_CFX_INDICES);
  },

  getCorporateCfxIndicesUrl(): string {
    return this.getUrl(URL.CORPORATE_CFX_INDICES);
  },

  getCfxIndexRulesMethodologyUrl(hash?: string): string {
    const base = this.getUrl(URL.CFX_INDEX_RULES_METHODOLOGY);
    return hash ? `${ base }#${ hash }` : base;
  },

  getFinsightApiUrl(uris: string | string[] = []): string {
    return buildUrl(domain.protocol, domain.finsightDomain, '/api', uris);
  },

  getStratsRpcUrl(): string {
    if (shouldUseInternalNetwork()) {
      return `${ creditFlowConfig.api.internal.finsightStrats }/rpc`;
    }

    return `${ creditFlowConfig.api.external }/strats/rpc`;
  },

  getRpcUrl({ internal = false }: { internal?: boolean } = {}): string {
    const path = internal ? '/rpc/internal' : '/rpc';

    if (shouldUseInternalNetwork()) {
      return `${ creditFlowConfig.api.internal.finsightWeb }${ path }`;
    }

    if (!isServer()) {
      return `${ this.getProxyPath() }${ path }`;
    }

    return `${ creditFlowConfig.api.external }${ path }`;
  },

  getCfrApiRpcUrl(): string {
    // cfr-api has no external URL by design: the server always talks to it over the internal network.
    if (isServer()) {
      return `${ creditFlowConfig.api.internal.cfrApi }/rpc`;
    }

    return `${ this.getBrowserOrigin() }/browser/cfr-api/rpc`;
  },

  getBrowserOrigin(): string {
    return `${ window.location.protocol }//${ window.location.hostname }`;
  },

  getProxyPath(): string {
    return `${ this.getBrowserOrigin() }/proxy/creditflowresearch`;
  },

  getAskAiRpcUrl(): string {
    return `${ domain.protocol }//${ domain.creditflowresearchDomain }/proxy/cfr-ask-ai/rpc`;
  },
};

export default url;
